import Fuse from "fuse.js/dist/fuse.esm.min";
// ///////////////////////////////////////////////////////////
// utils
// ///////////////////////////////////////////////////////////
function getParamsString(params) {
	return Object.keys(params)
		.map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
		.join("&");
}

export const stripHTML = (s) => s.replace(/<[^>]*>?/gm, "");

export const extractProperties = (geojson) => geojson?.features.map((f) => f.properties);

// The Levenshtein distance is a string metric for measuring the difference between two strings. Informally, the Levenshtein distance between two words is the minimum number of single-character edits (insertions, deletions or substitutions) required to change one word into the other.
const levenshteinDistance = (a, b) => {
	const matrix = [];

	let i;
	for (i = 0; i <= b.length; i++) {
		matrix[i] = [i];
	}

	let j;
	for (j = 0; j <= a.length; j++) {
		matrix[0][j] = j;
	}

	for (i = 1; i <= b.length; i++) {
		for (j = 1; j <= a.length; j++) {
			if (b.charAt(i - 1) === a.charAt(j - 1)) {
				matrix[i][j] = matrix[i - 1][j - 1];
			} else {
				matrix[i][j] = Math.min(matrix[i - 1][j - 1] + 1, Math.min(matrix[i][j - 1] + 1, matrix[i - 1][j] + 1));
			}
		}
	}

	return matrix[b.length][a.length];
};

// ///////////////////////////////////////////////////////////
// search providers
// ///////////////////////////////////////////////////////////

export class DataSearchProvider {
	constructor(opts) {
		this.options = {
			data: [],
			keys: null,
			getLabel: (d) => `${d}`,
			getLat: (d) => d.lat,
			getLng: (d) => d.lng,
			maxResults: 50,
			threshold: 0.2,
			icon: null,
			...opts,
		};

		this.fuse = new Fuse(this.options.data, {
			keys: this.options.keys,
			shouldSort: true,
			includeScore: true,
			threshold: this.options.threshold,
			minMatchCharLength: 2,
			useExtendedSearch: true,
			ignoreLocation: true,
		});
	}

	search(q) {
		let r = this.fuse.search(q);
		if (this.options.getFilter) r = r.filter(this.options.getFilter());
		if (this.options.maxResults) r = r.slice(0, this.options.maxResults);
		r = r.filter((d) => d.score <= this.options.threshold);
		r = r.map((d) => ({
			label: this.options.getLabel(d.item),
			datum: d.item,
			lat: this.options.getLat ? this.options.getLat(d.item) : d.item.lat,
			lng: this.options.getLng ? this.options.getLng(d.item) : d.item.lng,
			icon: this.options.icon,
			type: "data",
		}));
		return Promise.resolve(r);
	}
}

const hereApiKeys = {
	app_id: "IR3NpHxI7bi4WM0QrmdZ",
	app_code: "KoNsZD_GJ2YQNnC5k8o0yg",
};

function hereFilterCategories(r, q) {
	if (!r.category) return true;

	if (r.category.id === "administrative-region" && r.vicinity === "Deutschland") {
		return false;
	}
	if (
		["building", "street-square", "city-town-village", "postal-area", "administrative-region"].indexOf(
			r.category.id
		) !== -1
	) {
		return true;
	}
	if (r.category.system === "places") {
		const names = [r.title, ...(r?.alternativeNames || []).map((d) => d.name)];

		const dist = names.map((d) => levenshteinDistance(d.toLowerCase(), q.toLowerCase()));
		const minDist = Math.min(...dist);

		const contains = names.some((name) => name.toLowerCase().includes(q.toLowerCase()));
		if (minDist < 5 || contains) {
			return true;
		}
	}
	return false;
}

function hereGetLabel(r) {
	return r.title + (r.vicinity ? `, ${r.vicinity}` : "").replace("<br/>", ", ");
}

const hereURL = "https://places.api.here.com/places/v1/discover/search?{params}";

export class GeoSearchProvider {
	constructor(opts) {
		this.options = {
			bounds: [
				[180, 87],
				[-180, -87],
			],
			useMap: false,
			maxResults: 50,
			icon: null,
			...opts,
		};
	}

	search(q) {
		const b = this.options.bounds;
		const params = {
			q,
			...hereApiKeys,
		};
		if (this.options.useMap) {
			const c = this.options.useMap.map.getCenter();
			params.at = `${c.lat},${c.lng}`;
		} else {
			params.in = `${b[0][0]},${b[0][1]},${b[1][0]},${b[1][1]}`;
		}
		return fetch(hereURL.replace("{params}", getParamsString(params)))
			.then((r) => r.json())
			.then((r) => {
				const items = r.results.items
					.filter((d) => hereFilterCategories(d, q))
					.slice(0, this.options.maxResults)
					.map((d) => ({
						label: hereGetLabel(d),
						title: d.title,
						datum: d,
						lat: d.position[0],
						lng: d.position[1],
						icon: this.options.icon,
						type: "geo",
						isCity: d.category && d.category.id === "city-town-village",
					}));

				return items;
			});
	}
}

export function combineSearchProviders(ps) {
	return {
		search(q) {
			return Promise.all(ps.map((p) => p.search(q))).then((results) => Array.prototype.concat.apply([], results));
		},
	};
}
